/**
 * Formats a UUID for display by showing only the first and last parts
 * @param {string} uuid - The UUID to format
 * @param {number} startLength - Number of characters to show from the start (default: 8)
 * @param {number} endLength - Number of characters to show from the end (default: 8)
 * @returns {string} Formatted UUID string
 */
export const formatUUID = (uuid, startLength = 8, endLength = 8) => {
  if (!uuid || typeof uuid !== 'string') {
    return '';
  }
  
  // Remove hyphens for easier manipulation
  const cleanUuid = uuid.replace(/-/g, '');
  
  // If the UUID is shorter than or equal to the combined display length, show it all
  if (cleanUuid.length <= startLength + endLength) {
    return uuid;
  }
  
  const start = cleanUuid.slice(0, startLength);
  const end = cleanUuid.slice(-endLength);
  
  return `${start}...${end}`;
};

/**
 * React component for displaying a formatted UUID with optional tooltip showing full UUID
 * @param {Object} props
 * @param {string} props.uuid - The UUID to display
 * @param {number} props.startLength - Characters to show from start
 * @param {number} props.endLength - Characters to show from end
 * @param {boolean} props.showTooltip - Whether to show full UUID on hover
 * @param {string} props.className - Additional CSS classes
 */
export const UUIDDisplay = ({ 
  uuid, 
  startLength = 8, 
  endLength = 8, 
  showTooltip = true, 
  className = '' 
}) => {
  const formattedUuid = formatUUID(uuid, startLength, endLength);
  
  if (showTooltip) {
    return (
      <span 
        className={`font-mono cursor-help ${className}`}
        title={uuid}
      >
        {formattedUuid}
      </span>
    );
  }
  
  return (
    <span className={`font-mono ${className}`}>
      {formattedUuid}
    </span>
  );
};

// Usage examples:
/*
import { formatUUID, UUIDDisplay } from './uuid-utils';

// As a utility function
const shortId = formatUUID('d36a5c2e-93f0-4b57-8426-bf0c989a741b');
// Returns: "d36a5c2e...989a741b"

// As a React component
<UUIDDisplay uuid="d36a5c2e-93f0-4b57-8426-bf0c989a741b" />

// With custom lengths
<UUIDDisplay 
  uuid="d36a5c2e-93f0-4b57-8426-bf0c989a741b" 
  startLength={6} 
  endLength={6} 
/>

// Without tooltip
<UUIDDisplay 
  uuid="d36a5c2e-93f0-4b57-8426-bf0c989a741b" 
  showTooltip={false} 
/>
*/